import React from 'react';
import { Card } from '@/components/ui/card';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useUserRoles } from '@/hooks/useUserRoles';

interface LeaveRequestStats {
  approved: number;
  rejected: number;
  pending: number;
  total: number;
}

const ClientLeaveStatsChart = () => {
  const { hasRole } = useUserRoles();
  const isClient = hasRole ? hasRole('client') : false;

  const { data: leaveStats, isLoading } = useQuery({
    queryKey: ['client-leave-stats'],
    enabled: isClient,
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // Get client's assigned teams
      const { data: clientTeams } = await supabase
        .from('client_teams')
        .select('team_id')
        .eq('client_id', user.id);

      if (!clientTeams || clientTeams.length === 0) {
        return { approved: 0, rejected: 0, pending: 0, total: 0 };
      }

      const teamIds = clientTeams.map(t => t.team_id);

      // Get leave requests for these teams
      const { data: leaveRequests, error } = await supabase
        .from('leave_requests')
        .select('status')
        .in('team_id', teamIds);

      if (error) throw error;

      const stats: LeaveRequestStats = {
        approved: leaveRequests.filter(req => req.status === 'approved').length,
        rejected: leaveRequests.filter(req => req.status === 'rejected').length,
        pending: leaveRequests.filter(req => req.status === 'pending').length,
        total: leaveRequests.length
      };

      return stats;
    }
  });

  if (!isClient) return null;

  const chartData = [
    {
      name: 'Approved',
      value: leaveStats?.approved || 0,
      color: '#10B981'
    },
    {
      name: 'Rejected', 
      value: leaveStats?.rejected || 0,
      color: '#EF4444'
    },
    {
      name: 'Pending',
      value: leaveStats?.pending || 0,
      color: '#F59E0B'
    }
  ];

  const barData = [
    {
      status: 'Approved',
      count: leaveStats?.approved || 0,
      fill: '#10B981'
    },
    {
      status: 'Rejected',
      count: leaveStats?.rejected || 0,
      fill: '#EF4444'
    },
    {
      status: 'Pending',
      count: leaveStats?.pending || 0,
      fill: '#F59E0B'
    }
  ];

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Team Leave Request Statistics</h2>
            <p className="text-muted-foreground">
              Overview of leave request approvals vs rejections for your assigned teams
            </p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="h-64 flex items-center justify-center">
          <div className="text-muted-foreground">Loading statistics...</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Statistics Cards */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Card className="p-4 bg-green-50 border-green-200">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm text-green-600 font-medium">Approved</p>
                    <p className="text-2xl font-bold text-green-700">
                      {leaveStats?.approved || 0}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-4 bg-red-50 border-red-200">
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="text-sm text-red-600 font-medium">Rejected</p>
                    <p className="text-2xl font-bold text-red-700">
                      {leaveStats?.rejected || 0}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-4 bg-yellow-50 border-yellow-200">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-yellow-600" />
                  <div>
                    <p className="text-sm text-yellow-600 font-medium">Pending</p>
                    <p className="text-2xl font-bold text-yellow-700">
                      {leaveStats?.pending || 0}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-4 bg-blue-50 border-blue-200">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Total</p>
                    <p className="text-2xl font-bold text-blue-700">
                      {leaveStats?.total || 0}
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Approval Rate */}
            {leaveStats && leaveStats.total > 0 && (
              <Card className="p-4">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-1">Approval Rate</p>
                  <p className="text-3xl font-bold text-primary">
                    {Math.round((leaveStats.approved / leaveStats.total) * 100)}%
                  </p>
                </div>
              </Card>
            )}
          </div>

          {/* Bar Chart */}
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="status" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {leaveStats && leaveStats.total === 0 && (
        <div className="text-center py-8">
          <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No leave requests found for your assigned teams</p>
        </div>
      )}
    </Card>
  );
};

export default ClientLeaveStatsChart;